import {
  createFormatBlockCard,
  resolveFormatBlockMetadata,
} from '@/lib/idea-card-format-blocks';
import type { IdeaCardParserPhase } from '@/lib/idea-card-parser-phases';
import { finalizeParsedIdeaCard } from '@/lib/idea-card-parser-output';
import { parseSectionedHorizontalBody } from '@/lib/idea-card-text';
import type { IdeaCardInputData } from '@/lib/idea-card-types';

interface FormatBlockMatch {
  metadata: Record<string, string>;
  contentLines: string[];
  start: number;
  end: number;
  nextIndex: number;
}

const FORMAT_BLOCK_RE = /^\s*---format\s*$/;
const MALFORMED_FORMAT_RE = /^\s*---format\s+(.+)$/;

const stripMd = (value: string) => value.replace(/^\s*[\*\_\-\`~]+|[\*\_\-\`~]+\s*$/g, '').trim();

export const extractHorizontalBodySections = (
  layout: ReturnType<typeof resolveFormatBlockMetadata>['layout'],
  body?: string
) => (layout === 'horizontal' && body ? parseSectionedHorizontalBody(body) : undefined);

export const buildFormatBlockCard = (
  contentLines: string[],
  metadata: Record<string, string>
): IdeaCardInputData => {
  const nonEmptyIndexes = contentLines
    .map((line, index) => ({ line: line.trim(), index }))
    .filter(({ line }) => line.length > 0);
  const formatMetadata = resolveFormatBlockMetadata(metadata);

  if (nonEmptyIndexes.length === 0) {
    return createFormatBlockCard(metadata.title?.trim() || 'Card', formatMetadata, {
      body: undefined,
    });
  }

  const firstNonEmpty = nonEmptyIndexes[0];
  const rawFirstLine = firstNonEmpty.line;
  const inlineHeadingMatch = rawFirstLine.match(
    /^\s*(?:\d+[\.\)]\s*)?(?:\*\*|__)?([^:\n]+?)(?:\*\*|__)?\s*[:\uFF1A]\s*(.*)$/
  );

  let titleSource = metadata.title?.trim() || rawFirstLine;
  const bodyLines = [...contentLines];

  if (inlineHeadingMatch) {
    titleSource = inlineHeadingMatch[1].trim();
    const firstBodyLine = inlineHeadingMatch[2].trim();
    bodyLines[firstNonEmpty.index] = firstBodyLine;
  }

  const title = stripMd(titleSource).replace(/^\d+[\.\)]\s*/, '').trim() || 'Card';
  const body = bodyLines.join('\n').trim() || undefined;
  const bodySections = extractHorizontalBodySections(formatMetadata.layout, body);

  return createFormatBlockCard(title, formatMetadata, {
    body,
    bodySections,
  });
};

export const parseFormatBlockMetadataLines = (
  lines: string[],
  startIndex: number
): { metadata: Record<string, string>; nextIndex: number } => {
  const metadata: Record<string, string> = {};
  let nextIndex = startIndex;

  while (nextIndex < lines.length && lines[nextIndex].trim() !== '---') {
    const metaMatch = lines[nextIndex].match(/^(\w+):\s*(.+)$/);
    if (metaMatch) {
      metadata[metaMatch[1]] = metaMatch[2].trim();
    }
    nextIndex++;
  }

  if (nextIndex < lines.length && lines[nextIndex].trim() === '---') {
    nextIndex++;
  }

  return { metadata, nextIndex };
};

export const parseMalformedFormatBlockMetadata = (line: string): Record<string, string> => {
  const metadata: Record<string, string> = {};
  const match = line.match(MALFORMED_FORMAT_RE);

  if (match?.[1]) {
    const kvMatch = match[1].match(/^(\w+):\s*(.+)$/);
    if (kvMatch) {
      metadata[kvMatch[1]] = kvMatch[2].trim();
    }
  }

  return metadata;
};

export const findFormatBlockContentEnd = (
  lines: string[],
  startIndex: number
): { contentLines: string[]; end: number; nextIndex: number } => {
  let nextIndex = startIndex;

  while (
    nextIndex < lines.length &&
    !FORMAT_BLOCK_RE.test(lines[nextIndex]) &&
    !MALFORMED_FORMAT_RE.test(lines[nextIndex])
  ) {
    nextIndex++;
  }

  return {
    contentLines: lines.slice(startIndex, nextIndex),
    end: nextIndex - 1,
    nextIndex,
  };
};

export const matchFormatBlockAtLine = (
  lines: string[],
  lineIndex: number
): FormatBlockMatch | null => {
  if (FORMAT_BLOCK_RE.test(lines[lineIndex])) {
    const start = lineIndex;
    const { metadata, nextIndex: metadataEndIndex } = parseFormatBlockMetadataLines(lines, lineIndex + 1);
    const { contentLines, end, nextIndex } = findFormatBlockContentEnd(lines, metadataEndIndex);

    return {
      metadata,
      contentLines,
      start,
      end,
      nextIndex,
    };
  }

  if (MALFORMED_FORMAT_RE.test(lines[lineIndex])) {
    const start = lineIndex;
    const metadata = parseMalformedFormatBlockMetadata(lines[lineIndex]);
    const { contentLines, end, nextIndex } = findFormatBlockContentEnd(lines, lineIndex + 1);

    return {
      metadata,
      contentLines,
      start,
      end,
      nextIndex,
    };
  }

  return null;
};

export const parseFormatBlockPhase: IdeaCardParserPhase = context => {
  const cards: IdeaCardInputData[] = [];
  const usedRanges: Array<{ start: number; end: number }> = [];
  const { lines } = context;

  let lineIndex = 0;
  while (lineIndex < lines.length) {
    const match = matchFormatBlockAtLine(lines, lineIndex);
    if (!match) {
      lineIndex++;
      continue;
    }

    const content = match.contentLines.join('\n').trim();
    const hasUsefulMetadata = Boolean(match.metadata.title || match.metadata.type);

    if (content || hasUsefulMetadata) {
      cards.push(finalizeParsedIdeaCard(buildFormatBlockCard(match.contentLines, match.metadata)));
      usedRanges.push({ start: match.start, end: match.end });
    }

    lineIndex = match.nextIndex;
  }

  if (cards.length === 0) return null;

  return { cards, usedRanges, minimumCards: 1 };
};
